'use client';

import { useTranslation } from '@/hooks/useTranslation';
import Image from 'next/image';
import CTABanner from '@/components/blocks/CTABanner';
import React from 'react';

interface PageContentProps {
  locale: string;
}

export default function PageContent({ locale }: PageContentProps) {
  const { t } = useTranslation(locale);

  return (
    <div className="tw-relative tw-overflow-hidden tw-pb-4">
      <div className="tw-absolute tw-inset-0 tw-pointer-events-none animated-gradient"></div>

      <style jsx>{`
        .animated-gradient {
          background: linear-gradient(-45deg, #ee7752, #e73c7e, #23a6d5, #23d5ab);
          background-size: 400% 400%;
          animation: gradientShift 15s ease infinite;
          opacity: 0.1;
        }

        @keyframes gradientShift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
      `}</style>
      <div className="tw-why-video tw-flex tw-flex-col md:tw-p-0 tw-p-5 tw-mt-8 tw-mb-20">
        <div className="tw-flex-col tw-justify-center tw-items-center tw-gap-12 tw-flex">
          <div className="tw-flex-col tw-justify-center tw-items-center tw-gap-8 tw-flex">
            <div className="tw-flex-col tw-justify-center tw-items-center tw-gap-5 tw-flex">
              <h1 className="md:tw-w-[780px] tw-w-full tw-text-gray-700 tw-text-4xl tw-font-semibold tw-font-['Lexend'] tw-leading-10">
                {t('Advantage/title')}
              </h1>
            </div>
            <Image
              priority
              quality={100}
              className="md:tw-w-[780px] tw-w-full tw-h-full md:tw-h-[428px] tw-rounded-sm"
              src="https://cdn.1cdn.app/application/LOUISEBOT/2407071205390_1.png"
              alt="Unique Advantages of the Video Chatbot"
              title="Unique Advantages of the Video Chatbot"
              width={932}
              height={513}
            />
            <div className="tw-flex-col tw-justify-center tw-items-center tw-gap-3 tw-flex">
              <div className="md:tw-w-[780px] tw-w-full tw-text-gray-700 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
                {t('Advantage/h-01')}
              </div>
              <div className="md:tw-w-[780px] tw-w-full">
                <h2 className="tw-text-gray-700 tw-text-xl tw-font-bold tw-font-['Lexend'] tw-leading-normal">
                  {t('Advantage/t-01')}
                  <br />
                </h2>
                <div className="tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                  {t('Advantage/des-01')}
                  <br />
                </div>
                <h2 className="tw-text-gray-700 tw-mt-2 tw-text-xl tw-font-bold tw-font-['Lexend'] tw-leading-normal">
                  {t('Advantage/t-02')}
                  <br />
                </h2>
                <div className="tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                  {t('Advantage/des-02')}
                  <br />
                  <br />
                </div>
                <h2 className="tw-text-gray-700 tw-mt-2 tw-text-xl tw-font-bold tw-font-['Lexend'] tw-leading-normal">
                  {t('Advantage/t-03')}
                  <br />
                </h2>
                <div className="tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                  {t('Advantage/des-03')}
                </div>
              </div>
            </div>
            <div className="md:tw-w-[780px] tw-w-full tw-h-full md:tw-h-[428px] tw-relative">
              <Image
                priority
                quality={100}
                className="md:tw-w-[780px] tw-w-full tw-h-full md:tw-h-[428px] tw-rounded-sm"
                src="https://cdn.1cdn.app/application/LOUISEBOT/24070712053997_2.png"
                alt="Testimonials and Case Studies"
                title="Testimonials and Case Studies"
                width={934}
                height={513}
              />
            </div>
            <div className="md:tw-w-[780px] tw-w-full tw-flex tw-flex-col tw-items-center tw-gap-5">
              <h2 className="md:tw-w-[780px] tw-w-full tw-text-center tw-text-gray-700 tw-text-3xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
                {t('Advantage/CaseStudies/case')}
              </h2>
              <h3 className="md:tw-w-[780px] tw-w-full tw-text-center tw-text-gray-700 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
                {t('Advantage/CaseStudies/title')}
              </h3>
              <div className="tw-flex tw-flex-wrap tw-gap-5 tw-w-full tw-justify-center">
                <div className="tw-flex tw-flex-col tw-gap-5 tw-p-5 tw-bg-white tw-rounded-lg tw-shadow-md tw-grow tw-basis-0 tw-flex-1">
                  <h4 className="tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
                    {t('Advantage/CaseStudies/h-01')}
                  </h4>
                  <div className="tw-flex tw-flex-col tw-gap-3">
                    <div>
                      <div className="tw-text-gray-700 tw-text-base tw-font-bold">
                        {t('Advantage/CaseStudies/t-01')}
                      </div>
                      <div className="tw-text-gray-700 tw-text-base">{t('Advantage/CaseStudies/des-01')}</div>
                    </div>
                    <div>
                      <div className="tw-text-gray-700 tw-text-base tw-font-bold">
                        {t('Advantage/CaseStudies/t-02')}
                      </div>
                      <div className="tw-text-gray-700 tw-text-base">{t('Advantage/CaseStudies/des-02')}</div>
                    </div>
                    <div>
                      <div className="tw-text-gray-700 tw-text-base tw-font-bold">
                        {t('Advantage/CaseStudies/t-03')}
                      </div>
                      <div className="tw-text-gray-700 tw-text-base">
                        <div>{t('Advantage/CaseStudies/des-03')}</div>
                        <div>{t('Advantage/CaseStudies/des-031')}</div>
                        <div>{t('Advantage/CaseStudies/des-032')}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="tw-flex tw-flex-col tw-gap-5 tw-p-5 tw-bg-white tw-rounded-lg tw-shadow-md tw-grow tw-basis-0 tw-flex-1">
                  <h4 className="tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
                    {t('Advantage/CaseStudies/h-02')}
                  </h4>
                  <div className="tw-flex tw-flex-col tw-gap-3">
                    <div>
                      <div className="tw-text-gray-700 tw-text-base tw-font-bold">
                        {t('Advantage/CaseStudies/t-04')}
                      </div>
                      <div className="tw-text-gray-700 tw-text-base">{t('Advantage/CaseStudies/des-04')}</div>
                    </div>
                    <div>
                      <div className="tw-text-gray-700 tw-text-base tw-font-bold">
                        {t('Advantage/CaseStudies/t-05')}
                      </div>
                      <div className="tw-text-gray-700 tw-text-base">{t('Advantage/CaseStudies/des-05')}</div>
                    </div>
                    <div>
                      <div className="tw-text-gray-700 tw-text-base tw-font-bold">
                        {t('Advantage/CaseStudies/t-06')}
                      </div>
                      <div className="tw-text-gray-700 tw-text-base">
                        <div>{t('Advantage/CaseStudies/des-06')}</div>
                        <div>{t('Advantage/CaseStudies/des-061')}</div>
                        <div>{t('Advantage/CaseStudies/des-062')}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="tw-flex tw-flex-col tw-gap-5 tw-p-5 tw-bg-white tw-rounded-lg tw-shadow-md tw-grow tw-basis-0 tw-flex-1">
                  <h4 className="tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
                    {t('Advantage/CaseStudies/h-03')}
                  </h4>
                  <div className="tw-flex tw-flex-col tw-gap-3">
                    <div>
                      <div className="tw-text-gray-700 tw-text-base tw-font-bold">
                        {t('Advantage/CaseStudies/t-07')}
                      </div>
                      <div className="tw-text-gray-700 tw-text-base">{t('Advantage/CaseStudies/des-07')}</div>
                    </div>
                    <div>
                      <div className="tw-text-gray-700 tw-text-base tw-font-bold">
                        {t('Advantage/CaseStudies/t-08')}
                      </div>
                      <div className="tw-text-gray-700 tw-text-base">{t('Advantage/CaseStudies/des-08')}</div>
                    </div>
                    <div>
                      <div className="tw-text-gray-700 tw-text-base tw-font-bold">
                        {t('Advantage/CaseStudies/t-09')}
                      </div>
                      <div className="tw-text-gray-700 tw-text-base">
                        <div>{t('Advantage/CaseStudies/des-09')}</div>
                        <div>{t('Advantage/CaseStudies/des-091')}</div>
                        <div>{t('Advantage/CaseStudies/des-092')}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="tw-flex-col tw-justify-center tw-items-center tw-gap-8 tw-flex">
              <Image
                priority
                quality={100}
                className="md:tw-w-[780px] tw-w-full md:tw-h-[428px] tw-h-full tw-rounded-sm"
                src="https://cdn.1cdn.app/application/LOUISEBOT/24070213295067_image2.png"
                alt="Testimonials and Case Studies"
                title="Testimonials and Case Studies"
                width={980}
                height={513}
              />
              <div className="md:tw-w-[780px] tw-w-full tw-flex-col tw-justify-center tw-items-center tw-gap-6 tw-flex">
                <section className="tw-w-full md:tw-h-[92px] tw-h-full tw-flex-col tw-justify-center tw-items-center tw-gap-3 tw-flex">
                  <h2 className="tw-text-center tw-text-gray-700 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
                    {t('Advantage/TestimonialsandCaseStudies/title')}
                  </h2>
                  <div className="tw-text-center tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                    {t('Advantage/TestimonialsandCaseStudies/des')}
                  </div>
                </section>

                <section className="tw-self-stretch tw-justify-center tw-items-start tw-gap-8 md:tw-flex tw-flex-col md:tw-flex-row">
                  <div className="tw-grow tw-shrink tw-basis-0 tw-flex-col tw-justify-center tw-items-center tw-gap-3 tw-flex">
                    <cite className="tw-self-stretch tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                      {t('Advantage/TestimonialsandCaseStudies/des-01')}
                    </cite>
                    <div className="tw-self-stretch tw-text-gray-500 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal tw-mb-2">
                      {t('Advantage/TestimonialsandCaseStudies/t-01')}
                    </div>
                  </div>

                  <div className="tw-grow tw-shrink tw-basis-0 tw-flex-col tw-justify-center tw-items-center tw-gap-3 tw-flex">
                    <cite className="tw-self-stretch tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                      {t('Advantage/TestimonialsandCaseStudies/des-02')}
                    </cite>
                    <div className="tw-self-stretch tw-text-gray-500 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal tw-mb-2">
                      {t('Advantage/TestimonialsandCaseStudies/t-02')}
                    </div>
                  </div>

                  <div className="tw-grow tw-shrink tw-basis-0 tw-flex-col tw-justify-center tw-items-center tw-gap-3 tw-flex">
                    <cite className="tw-self-stretch tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                      {t('Advantage/TestimonialsandCaseStudies/des-03')}
                    </cite>
                    <div className="tw-self-stretch tw-text-gray-500 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                      {t('Advantage/TestimonialsandCaseStudies/t-03')}
                    </div>
                  </div>
                </section>
              </div>
              <Image
                priority
                quality={100}
                className="md:tw-w-[780px] tw-w-full md:tw-h-[428px] tw-h-full tw-rounded-sm"
                src="https://cdn.1cdn.app/application/LOUISEBOT/24070213295051_image_(2).png"
                alt="Interactive Comparison"
                title="Interactive Comparison"
                width={912}
                height={513}
              />
              <div className="tw-flex-col tw-justify-center tw-items-center tw-gap-3 tw-flex">
                <h2 className="md:tw-w-[780px] tw-w-full tw-text-gray-700 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
                  {t('Advantage/InteractiveComparison/title')}
                </h2>

                <div className="md:tw-w-[780px] tw-w-full">
                  <div className="tw-text-gray-700 tw-text-base tw-font-bold tw-font-['Lexend'] tw-leading-normal">
                    <h3>{t('Advantage/InteractiveComparison/t-01')}</h3>
                  </div>
                  <div className="tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                    {t('Advantage/InteractiveComparison/des-01')}
                  </div>
                </div>

                <div className="md:tw-w-[780px] tw-w-full">
                  <div className="tw-text-gray-700 tw-text-base tw-font-bold tw-font-['Lexend'] tw-leading-normal">
                    <h3>{t('Advantage/InteractiveComparison/t-02')}</h3>
                  </div>
                  <div className="tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                    {t('Advantage/InteractiveComparison/des-02')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CTABanner
        title={t('Home/ReadyToGetStarted/cta-title')}
        description={t('Home/ReadyToGetStarted/cta-description')}
        cta={t('Home/ReadyToGetStarted/start-trial-aria')}
        ctaTarget="register"
        locale={locale}
      />
    </div>
  );
}
